import { AdminDashboardDto, DashboardRegionTypeDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useApiClient } from '@/api/util/useApiClient.util.ts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

export interface DashboardFilter {
  // yyyy-mm-dd, inclusive; without it the period starts at the first candidate
  from?: string;
  region?: DashboardRegionFilter;
  // yyyy-mm-dd, inclusive; without it the period ends today
  to?: string;
}

export interface DashboardRegionFilter {
  // ISO 3166-2 code, e.g. PL-14
  code: string;
  type: DashboardRegionTypeDto;
}

interface GetAdminDashboardParams {
  adminUserId: string;
  filter: DashboardFilter;
  // milliseconds; false turns automatic refreshing off
  refetchInterval: false | number;
}

export const useGetAdminDashboard = ({
  adminUserId,
  filter,
  refetchInterval,
}: GetAdminDashboardParams) => {
  const { adminDashboardApi } = useApiClient();

  const query = useQuery<AdminDashboardDto>({
    enabled: !!adminUserId,
    // a new filter keeps the previous charts on screen until the new numbers arrive
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const response = await adminDashboardApi.getAdminDashboard(
        adminUserId,
        filter.from,
        filter.to,
        filter.region?.type,
        filter.region?.code,
      );
      return response.data;
    },
    queryKey: [QueryKeyEnum.ADMIN_DASHBOARD, adminUserId, filter],
    refetchInterval,
  });

  return {
    dashboard: query.data,
    error: query.error,
    isFetching: query.isFetching,
    updatedAt: query.dataUpdatedAt,
  };
};
